import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addCampaignMaterial,
  cancelCampaignPreview,
  createCampaign,
  createCampaignDirectRun,
  createCampaignPreview,
  createCampaignRun,
  createCampaignTextRepairPreview,
  deleteCampaignMaterial,
  extractCampaign,
  getCampaign,
  getCampaignPreview,
  getCampaignPreviewCandidateCount,
  getCampaignRun,
  getEnvironmentRoutes,
  listCampaigns,
  pauseCampaignPreview,
  resumeCampaignPreview,
  updateCampaign,
} from '../../api/adminApi.js';
import type {
  AdminCurrentUser,
  EnvironmentRouteAccount,
  Campaign,
  CampaignActiveCampaign,
  CampaignDetailResponse,
  CampaignMaterial,
  CampaignPreview,
  CampaignPreviewItem,
  CampaignRunItem,
} from '../../api/types.js';
import '../../styles/campaigns.css';

interface CampaignConsoleProps {
  user: AdminCurrentUser;
  onToast: (message: string, type?: 'info' | 'success' | 'error') => void;
  onBackToChat: () => void;
}

const CONFIRM_TEXT = '确认启动';
const CONFIRMED_FIELDS = ['title', 'main_category', 'price_mechanism', 'selling_point', 'valid_until'];
const PREVIEW_PAGE_SIZE = 25;
const PREVIEW_CONCURRENCY_DEFAULT = 5;
const PREVIEW_CONCURRENCY_MAX = 100;

interface UploadingMaterial {
  id: string;
  filename: string;
  status: 'uploading' | 'failed';
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: '草稿',
    ready: '已整理',
    previewed: '已预览',
    active: '发送中',
    paused: '已暂停',
    archived: '已归档',
    completed: '已完成',
    running: '处理中',
    failed: '失败',
    cancelled: '已停止',
    eligible: '可发送',
    blocked: '不可发送',
    needs_review: '需查看',
    queued: '排队中',
    held: '暂缓发送',
    processing: '发送处理中',
    skipped: '已跳过',
    sent: '已发送',
    vetoed: '发送拦截',
    partial_failed: '部分失败',
    replied: '已回复',
  };
  return labels[status] ?? status;
}

function formatBot(account: EnvironmentRouteAccount): string {
  const name = account.nickname || account.wechatId || '未命名';
  return `${name} (${account.wechatRecordId})`;
}

function toIsoEndOfDay(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return null;
}

function detailToDraft(detail: CampaignDetailResponse): CampaignActiveCampaign {
  return detail.campaign.activeCampaign ?? {};
}

function latestPreview(previews: CampaignPreview[]): CampaignPreview | null {
  return previews[0] ?? null;
}

function latestRunForPreview(detail: CampaignDetailResponse, previewId: number | null): number | null {
  if (!previewId) return null;
  return detail.runs.find((run) => run.previewId === previewId)?.id ?? null;
}

function inputValue(value: string | null | undefined): string {
  return value ?? '';
}

function parsePreviewConcurrency(value: string): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return PREVIEW_CONCURRENCY_DEFAULT;
  return Math.max(1, Math.min(parsed, PREVIEW_CONCURRENCY_MAX));
}

function makeDefaultDraft(activityText: string): CampaignActiveCampaign {
  const title = activityText.trim().split(/\n+/)[0]?.slice(0, 40);
  return title ? { title } : {};
}

function previewBlockReason(input: {
  readonly: boolean;
  campaign: Campaign | null;
  selectedBotIds: number[];
  draft: CampaignActiveCampaign;
}): string | null {
  if (input.readonly) return '当前账号只能查看，不能生成 dry run。';
  if (!input.campaign) return '先创建或保存活动。';
  if (input.selectedBotIds.length === 0) return '先选择至少一个销售微信/IP。';
  if (!inputValue(input.draft.title || input.campaign.title).trim()) return '先填写活动标题。';
  if (!inputValue(input.draft.main_category).trim()) return '先填写主推品类。';
  if (!inputValue(input.draft.price_mechanism).trim()) return '先填写价格机制。';
  if (!inputValue(input.draft.selling_point).trim()) return '先填写核心卖点。';
  return null;
}

function sendBlockReason(input: { readonly: boolean; preview: CampaignPreview | null }): string | null {
  if (input.readonly) return '当前账号只能查看，不能发送。';
  if (!input.preview) return '先生成 dry run。';
  if (input.preview.status !== 'completed') return 'dry run 完成后才能发送。';
  if (input.preview.eligibleCount <= 0) return 'dry run 里没有可发送客户。';
  return null;
}

function nextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

export function CampaignConsole({ user, onToast, onBackToChat }: CampaignConsoleProps) {
  const readonly = user.role !== 'admin';
  const [bots, setBots] = useState<EnvironmentRouteAccount[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [materials, setMaterials] = useState<CampaignMaterial[]>([]);
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [previewItems, setPreviewItems] = useState<CampaignPreviewItem[]>([]);
  const [runItems, setRunItems] = useState<CampaignRunItem[]>([]);
  const [activityText, setActivityText] = useState('');
  const [selectedBotIds, setSelectedBotIds] = useState<number[]>([]);
  const [draft, setDraft] = useState<CampaignActiveCampaign>({});
  const [tone, setTone] = useState('自然、像老朋友提醒，不要像广告群发');
  const [validUntil, setValidUntil] = useState('');
  const [previewLimit, setPreviewLimit] = useState('5');
  const [previewConcurrency, setPreviewConcurrency] = useState(String(PREVIEW_CONCURRENCY_DEFAULT));
  const [previewAll, setPreviewAll] = useState(false);
  const [candidateCount, setCandidateCount] = useState<number | null>(null);
  const [previewPage, setPreviewPage] = useState(0);
  const previewPageRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [organizeStep, setOrganizeStep] = useState<string | null>(null);
  const [previewStep, setPreviewStep] = useState<string | null>(null);
  const [uploadingMaterials, setUploadingMaterials] = useState<UploadingMaterial[]>([]);
  const [deletingMaterialId, setDeletingMaterialId] = useState<number | null>(null);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [directSendConfirmOpen, setDirectSendConfirmOpen] = useState(false);

  const selectableBots = useMemo(() => {
    const allowed = user.allowedBotRecordIds ? new Set(user.allowedBotRecordIds) : null;
    return bots.filter((bot) => bot.bridgeAllowed && (!allowed || allowed.has(bot.wechatRecordId)));
  }, [bots, user.allowedBotRecordIds]);

  const selectedBots = useMemo(
    () => selectableBots.filter((bot) => selectedBotIds.includes(bot.wechatRecordId)),
    [selectableBots, selectedBotIds],
  );

  const allSelected = selectableBots.length > 0 && selectedBotIds.length === selectableBots.length;
  const hasReadyDraft = Boolean(
    campaign
    && selectedBotIds.length > 0
    && inputValue(draft.title || campaign.title).trim()
    && inputValue(draft.main_category).trim()
    && inputValue(draft.price_mechanism).trim()
    && inputValue(draft.selling_point).trim(),
  );
  const canPreview = !readonly && hasReadyDraft;
  const canSend = !readonly && Boolean(campaign && preview && preview.status === 'completed' && preview.eligibleCount > 0);
  const previewBusy = Boolean(previewStep);
  const nonPreviewBusy = busy && !previewBusy;
  const previewBlockedReason = previewBlockReason({
    readonly,
    campaign,
    selectedBotIds,
    draft,
  });
  const sendBlockedReason = sendBlockReason({ readonly, preview });
  const activeStep = organizeStep ?? previewStep;

  function updatePreviewPage(page: number) {
    previewPageRef.current = page;
    setPreviewPage(page);
  }

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [botResponse, campaignResponse] = await Promise.all([
        getEnvironmentRoutes(),
        listCampaigns(),
      ]);
      const visibleBots = botResponse.accounts.filter((bot) => bot.bridgeAllowed);
      setBots(visibleBots);
      setCampaigns(campaignResponse.campaigns);
      const newestEditable = campaignResponse.campaigns.find((item) => item.status !== 'archived') ?? null;
      if (newestEditable) {
        await applyCampaign(newestEditable.id);
      } else {
        const allowed = user.allowedBotRecordIds ? new Set(user.allowedBotRecordIds) : null;
        const defaultBots = visibleBots
          .filter((bot) => !allowed || allowed.has(bot.wechatRecordId))
          .map((bot) => bot.wechatRecordId);
        setSelectedBotIds(defaultBots);
      }
    } catch (error) {
      onToast(error instanceof Error ? error.message : '活动助手加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [onToast, user.allowedBotRecordIds]);

  const applyDetail = useCallback(async (detail: CampaignDetailResponse) => {
    setCampaign(detail.campaign);
    setMaterials(detail.materials);
    setDraft(detailToDraft(detail));
    setSelectedBotIds(detail.campaign.botWechatRecordIds);
    setValidUntil(detail.campaign.activeCampaign?.valid_until ?? detail.campaign.validUntil ?? '');
    const foundPreview = latestPreview(detail.previews);
    setPreview(foundPreview);
    updatePreviewPage(0);
    if (foundPreview) {
      const previewResponse = await getCampaignPreview(detail.campaign.id, foundPreview.id, { limit: PREVIEW_PAGE_SIZE, offset: 0 });
      setPreviewItems(previewResponse.items);
      await loadRunItemsForPreview(detail, foundPreview.id);
      if (foundPreview.status === 'running') {
        setBusy(true);
        setPreviewStep('判断官和销售 AI 正在逐个客户 dry run（刷新后已恢复）');
        void resumePreviewPolling(detail.campaign.id, foundPreview.id);
      } else {
        setPreviewStep(null);
        setBusy(false);
      }
    } else {
      setPreviewItems([]);
      setRunItems([]);
    }
    if (!readonly) {
      getCampaignPreviewCandidateCount(detail.campaign.id)
        .then((response) => setCandidateCount(response.candidateCount))
        .catch(() => setCandidateCount(null));
    }
  }, [readonly]);

  const applyCampaign = useCallback(async (campaignId: number) => {
    const detail = await getCampaign(campaignId);
    await applyDetail(detail);
  }, [applyDetail]);

  function resetToNewCampaign() {
    setCampaign(null);
    setMaterials([]);
    setPreview(null);
    setPreviewItems([]);
    setRunItems([]);
    setActivityText('');
    setDraft({});
    setValidUntil('');
    setCandidateCount(null);
    updatePreviewPage(0);
    setSelectedBotIds(selectableBots.map((bot) => bot.wechatRecordId));
  }

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  function patchDraft(patch: Partial<CampaignActiveCampaign>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function toggleBot(recordId: number, checked: boolean) {
    setSelectedBotIds((current) => (
      checked ? [...new Set([...current, recordId])] : current.filter((id) => id !== recordId)
    ));
  }

  function toggleAllBots(checked: boolean) {
    setSelectedBotIds(checked ? selectableBots.map((bot) => bot.wechatRecordId) : []);
  }

  async function ensureCampaign(): Promise<CampaignDetailResponse> {
    if (selectedBotIds.length === 0) {
      throw new Error('请选择至少一个销售微信/IP');
    }
    const title = inputValue(draft.title || makeDefaultDraft(activityText).title || '未命名活动').trim();
    if (!campaign) {
      return createCampaign({
        ipName: selectedBots[0]?.nickname || selectedBots[0]?.wechatId || '活动助手',
        botWechatRecordIds: selectedBotIds,
        initialText: activityText.trim() || title,
      });
    }
    return updateCampaign(campaign.id, {
      title,
      ipName: selectedBots[0]?.nickname || selectedBots[0]?.wechatId || campaign.ipName,
      botWechatRecordIds: selectedBotIds,
      activeCampaign: {
        ...draft,
        title,
        valid_until: draft.valid_until || validUntil || null,
        audience_hint: tone.trim() || draft.audience_hint,
        materials: materials.filter((item) => item.sendable).map((item) => item.title || item.filename || `material:${item.id}`),
      },
      confirmedFields: CONFIRMED_FIELDS,
      extractionWarnings: [],
      validUntil: toIsoEndOfDay(draft.valid_until || validUntil),
    });
  }

  async function handleOrganize() {
    if (readonly) return;
    if (!activityText.trim() && !draft.title?.trim()) {
      onToast('先填活动文案', 'error');
      return;
    }
    setBusy(true);
    setOrganizeStep('正在保存活动信息');
    try {
      await nextPaint();
      const saved = await ensureCampaign();
      setOrganizeStep(materials.length > 0 ? '正在启动图片读取和字段整理' : '正在启动活动字段整理');
      const extracted = await extractCampaign(saved.campaign.id, { message: activityText.trim() });
      await applyDetail(extracted);
      setOrganizeStep(materials.length > 0 ? '正在读取图片并整理字段' : '正在整理活动字段');
      const completed = await pollExtractionUntilDone(saved.campaign.id, (attempt) => {
        setOrganizeStep(`${materials.length > 0 ? '正在读取图片并整理字段' : '正在整理活动字段'}（已等待 ${attempt * 2} 秒）`);
      });
      if (extractStatus(completed.campaign) === 'failed') {
        throw new Error(extractError(completed.campaign) || completed.campaign.extractionWarnings?.[0] || 'Kimi 活动抽取失败');
      }
      setOrganizeStep('正在回填右侧字段');
      await applyDetail(completed);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('AI 已整理好活动，可继续手动编辑', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '整理失败', 'error');
    } finally {
      setOrganizeStep(null);
      setBusy(false);
    }
  }

  async function handleSaveEdits(): Promise<CampaignDetailResponse | null> {
    if (readonly) return null;
    setBusy(true);
    try {
      const saved = await ensureCampaign();
      await applyDetail(saved);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('活动已保存', 'success');
      return saved;
    } catch (error) {
      onToast(error instanceof Error ? error.message : '保存失败', 'error');
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function handleImageUpload(event: ChangeEvent<HTMLInputElement>) {
    if (readonly) return;
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    const uploadItems = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      filename: file.name,
      status: 'uploading' as const,
    }));
    setUploadingMaterials((current) => [...current, ...uploadItems]);
    try {
      const saved = await ensureCampaign();
      for (const [index, file] of files.entries()) {
        const uploadItem = uploadItems[index];
        if (!file.type.startsWith('image/')) {
          onToast(`${file.name} 不是图片，已跳过`, 'error');
          setUploadingMaterials((current) => current.map((item) => item.id === uploadItem.id ? { ...item, status: 'failed' } : item));
          continue;
        }
        const base64 = await fileToBase64(file);
        try {
          const response = await addCampaignMaterial(saved.campaign.id, {
            mediaType: file.type || 'image/png',
            title: file.name,
            filename: file.name,
            base64,
            sendable: true,
          });
          response.warnings?.forEach((warning) => onToast(warning, 'error'));
          setUploadingMaterials((current) => current.filter((item) => item.id !== uploadItem.id));
        } catch (error) {
          setUploadingMaterials((current) => current.map((item) => item.id === uploadItem.id ? { ...item, status: 'failed' } : item));
          throw error;
        }
      }
      await applyCampaign(saved.campaign.id);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('图片已上传', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '图片上传失败', 'error');
    } finally {
      event.target.value = '';
    }
  }

  async function handleDeleteMaterial(material: CampaignMaterial) {
    if (readonly || !campaign || deletingMaterialId) return;
    setDeletingMaterialId(material.id);
    try {
      await deleteCampaignMaterial(campaign.id, material.id);
      await applyCampaign(campaign.id);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('图片已删除', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '图片删除失败', 'error');
    } finally {
      setDeletingMaterialId(null);
    }
  }

  async function handlePreview() {
    if (!canPreview || !campaign) {
      onToast('先完成活动整理和必填字段', 'error');
      return;
    }
    setBusy(true);
    setPreviewStep('正在保存活动字段');
    try {
      await nextPaint();
      const saved = await ensureCampaign();
      setPreview(null);
      setPreviewItems([]);
      updatePreviewPage(0);
      setPreviewStep('正在启动判断官和销售 AI 预演');
      const limit = previewAll ? 0 : Number(previewLimit) || 5;
      const concurrency = parsePreviewConcurrency(previewConcurrency);
      const created = await createCampaignPreview(saved.campaign.id, { limit, sampleSize: Math.min(limit || 10, 10), concurrency });
      setPreview(created.preview);
      setPreviewItems(created.items);
      setPreviewStep('判断官和销售 AI 正在逐个客户 dry run');
      const previewResponse = await pollPreviewUntilDone(saved.campaign.id, created.preview.id, previewPageRef, (attempt) => {
        setPreviewStep(`判断官和销售 AI 正在逐个客户 dry run（已等待 ${attempt * 2} 秒）`);
      }, applyPreviewPollUpdate);
      if (previewResponse.preview.status === 'failed') {
        const error = typeof previewResponse.preview.summary?.error === 'string'
          ? previewResponse.preview.summary.error
          : 'dry run 后台任务失败';
        throw new Error(error);
      }
      if (previewResponse.preview.status === 'paused') {
        onToast('dry run 已暂停，可以稍后继续', 'info');
        return;
      }
      if (previewResponse.preview.status === 'cancelled') {
        onToast('dry run 已停止', 'info');
        return;
      }
      setPreview(previewResponse.preview);
      setPreviewItems(previewResponse.items);
      setPreviewStep('正在回填预览列表');
      await applyCampaign(saved.campaign.id);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('dry run 已生成，没有发送任何消息', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '生成文案失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  async function handleTextRepairPreview() {
    if (!canPreview || !campaign) {
      onToast('先完成活动整理和必填字段', 'error');
      return;
    }
    setBusy(true);
    setPreviewStep('正在启动缺失文案补发预览');
    try {
      await nextPaint();
      const saved = await ensureCampaign();
      setPreview(null);
      setPreviewItems([]);
      updatePreviewPage(0);
      const limit = previewAll ? 0 : Number(previewLimit) || 5;
      const concurrency = parsePreviewConcurrency(previewConcurrency);
      const created = await createCampaignTextRepairPreview(saved.campaign.id, {
        limit,
        sampleSize: Math.min(limit || 10, 10),
        concurrency,
      });
      setPreview(created.preview);
      setPreviewItems(created.items);
      setPreviewStep('销售 AI 正在只生成缺失文字，不会补发图片');
      const previewResponse = await pollPreviewUntilDone(saved.campaign.id, created.preview.id, previewPageRef, (attempt) => {
        setPreviewStep(`销售 AI 正在生成缺失文字（已等待 ${attempt * 2} 秒）`);
      }, applyPreviewPollUpdate);
      if (previewResponse.preview.status === 'failed') {
        throw new Error(previewError(previewResponse.preview));
      }
      setPreview(previewResponse.preview);
      setPreviewItems(previewResponse.items);
      setPreviewStep('正在回填补发预览列表');
      await applyCampaign(saved.campaign.id);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('缺失文案补发预览已生成，没有发送任何消息', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '补发预览失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  async function resumePreviewPolling(campaignId: number, previewId: number) {
    try {
      const previewResponse = await pollPreviewUntilDone(campaignId, previewId, previewPageRef, (attempt) => {
        setPreviewStep(`判断官和销售 AI 正在逐个客户 dry run（已等待 ${attempt * 2} 秒）`);
      }, applyPreviewPollUpdate);
      if (previewResponse.preview.status === 'failed') {
        const error = typeof previewResponse.preview.summary?.error === 'string'
          ? previewResponse.preview.summary.error
          : 'dry run 后台任务失败';
        onToast(error, 'error');
        return;
      }
      if (previewResponse.preview.status === 'paused') {
        onToast('dry run 已暂停，可以稍后继续', 'info');
        return;
      }
      if (previewResponse.preview.status === 'cancelled') {
        onToast('dry run 已停止', 'info');
        return;
      }
      setPreview(previewResponse.preview);
      setPreviewItems(previewResponse.items);
      onToast('dry run 已生成，没有发送任何消息', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : 'dry run 状态刷新失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  function applyPreviewPollUpdate(response: Awaited<ReturnType<typeof getCampaignPreview>>) {
    setPreview(response.preview);
    setPreviewItems(response.items);
  }

  async function loadRunItemsForPreview(detail: CampaignDetailResponse, previewId: number) {
    const runId = latestRunForPreview(detail, previewId);
    if (!runId) {
      setRunItems([]);
      return;
    }
    const response = await getCampaignRun(detail.campaign.id, runId, { limit: 500, offset: 0 });
    setRunItems(response.items ?? []);
  }

  async function loadPreviewPage(nextPage: number) {
    if (!campaign || !preview) return;
    const safePage = Math.max(0, nextPage);
    const response = await getCampaignPreview(campaign.id, preview.id, {
      limit: PREVIEW_PAGE_SIZE,
      offset: safePage * PREVIEW_PAGE_SIZE,
    });
    setPreview(response.preview);
    setPreviewItems(response.items);
    updatePreviewPage(safePage);
  }

  async function handlePausePreview() {
    if (!campaign || !preview || preview.status !== 'running') return;
    setBusy(true);
    setPreviewStep('正在暂停 dry run');
    try {
      const response = await pauseCampaignPreview(campaign.id, preview.id);
      setPreview(response.preview);
      setPreviewItems(response.items);
      onToast('dry run 已暂停', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '暂停失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  async function handleResumePreview() {
    if (!campaign || !preview || (preview.status !== 'paused' && preview.status !== 'failed')) return;
    setBusy(true);
    setPreviewStep('正在继续 dry run');
    try {
      const response = await resumeCampaignPreview(campaign.id, preview.id);
      setPreview(response.preview);
      setPreviewItems(response.items);
      setPreviewStep('判断官和销售 AI 正在逐个客户 dry run');
      const completed = await pollPreviewUntilDone(campaign.id, preview.id, previewPageRef, (attempt) => {
        setPreviewStep(`判断官和销售 AI 正在逐个客户 dry run（已等待 ${attempt * 2} 秒）`);
      }, applyPreviewPollUpdate);
      setPreview(completed.preview);
      setPreviewItems(completed.items);
      if (completed.preview.status === 'completed') onToast('dry run 已生成，没有发送任何消息', 'success');
      if (completed.preview.status === 'paused') onToast('dry run 已暂停，可以稍后继续', 'info');
      if (completed.preview.status === 'cancelled') onToast('dry run 已停止', 'info');
      if (completed.preview.status === 'failed') onToast(previewError(completed.preview), 'error');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '继续失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  async function handleCancelPreview() {
    if (!campaign || !preview || (preview.status !== 'running' && preview.status !== 'paused')) return;
    setBusy(true);
    setPreviewStep('正在停止 dry run');
    try {
      const response = await cancelCampaignPreview(campaign.id, preview.id);
      setPreview(response.preview);
      setPreviewItems(response.items);
      onToast('dry run 已停止', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '停止失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  async function handleSend() {
    if (!campaign || !preview || !canSend) return;
    setBusy(true);
    try {
      await createCampaignRun(campaign.id, {
        previewId: preview.id,
        confirmText: CONFIRM_TEXT,
        rateLimit: {
          maxPerHour: 20,
          maxPerRun: preview.eligibleCount,
          randomDelaySeconds: [30, 90],
        },
      });
      await applyCampaign(campaign.id);
      setCampaigns((await listCampaigns()).campaigns);
      setSendConfirmOpen(false);
      onToast('已开始发送，系统会按节奏执行', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '发送失败', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleDirectRunAndSend() {
    if (!canPreview || !campaign) {
      onToast('先完成活动整理和必填字段', 'error');
      return;
    }
    setBusy(true);
    setPreviewStep('正在保存活动字段');
    try {
      await nextPaint();
      const saved = await ensureCampaign();
      setPreview(null);
      setPreviewItems([]);
      updatePreviewPage(0);
      const limit = previewAll ? 0 : Number(previewLimit) || 5;
      const concurrency = parsePreviewConcurrency(previewConcurrency);
      setPreviewStep('正在启动生成并发送任务');
      const created = await createCampaignDirectRun(saved.campaign.id, {
        limit,
        concurrency,
        confirmText: CONFIRM_TEXT,
        rateLimit: {
          maxPerHour: 20,
          maxPerRun: limit || Math.max(candidateCount ?? 1, 1),
          randomDelaySeconds: [0, 0],
        },
      });
      setPreview(created.preview);
      setPreviewItems(created.items);
      setDirectSendConfirmOpen(false);
      setPreviewStep('判断官和销售 AI 正在逐个客户生成并发送');
      const previewResponse = await pollPreviewUntilDone(saved.campaign.id, created.preview.id, previewPageRef, (attempt) => {
        setPreviewStep(`判断官和销售 AI 正在逐个客户生成并发送（已等待 ${attempt * 2} 秒）`);
      }, applyPreviewPollUpdate);
      if (previewResponse.preview.status === 'failed') {
        throw new Error(previewError(previewResponse.preview));
      }
      setPreview(previewResponse.preview);
      setPreviewItems(previewResponse.items);
      await applyCampaign(saved.campaign.id);
      setCampaigns((await listCampaigns()).campaigns);
      onToast('生成并发送已完成', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '生成并发送失败', 'error');
    } finally {
      setPreviewStep(null);
      setBusy(false);
    }
  }

  return (
    <main className="campaign-simple-shell">
      <header className="campaign-simple-header">
        <div>
          <h1>活动助手</h1>
          <p>填活动，AI 整理，预览文案，再发送。</p>
        </div>
        <div className="campaign-header-actions">
          <select
            value={campaign?.id ?? ''}
            onChange={(event) => {
              const id = Number(event.target.value);
              if (id) {
                void applyCampaign(id);
              } else {
                resetToNewCampaign();
              }
            }}
          >
            <option value="">新活动</option>
            {campaigns.map((item) => (
              <option key={item.id} value={item.id}>{item.title} · {statusLabel(item.status)}</option>
            ))}
          </select>
          <button className="button" type="button" onClick={onBackToChat}>返回会话</button>
        </div>
      </header>

      {readonly ? <div className="campaign-warning inline">当前账号只能查看活动，不能整理或发送。</div> : null}
      {activeStep ? <div className="campaign-global-progress"><ProgressNotice label={activeStep} /></div> : null}
      {loading ? <div className="campaign-loading">活动助手加载中...</div> : null}

      <section className="campaign-workspace">
        <div className="campaign-prep-column">
        <section className="campaign-step compact">
          <div className="campaign-step-title">
            <span>1</span>
            <h2>填活动</h2>
          </div>
          <label>
            <span>活动文案</span>
            <textarea
              value={activityText}
              onChange={(event) => {
                setActivityText(event.target.value);
                if (!campaign && !draft.title) patchDraft(makeDefaultDraft(event.target.value));
              }}
              rows={5}
              placeholder="把老板给你的活动说明直接粘进来，比如主推品类、价格、赠品、有效期、适合什么客户。"
              disabled={readonly || busy}
            />
          </label>
          <label>
            <span>图片</span>
            <input type="file" accept="image/*" multiple onChange={handleImageUpload} disabled={readonly || selectedBotIds.length === 0} />
          </label>
          <div className="material-strip">
            {materials.length === 0 && uploadingMaterials.length === 0 ? <span>还没有图片</span> : null}
            {materials.map((item) => (
              <div className="material-chip" key={item.id}>
                <a href={item.mediaUrl ?? '#'} target="_blank" rel="noreferrer">{item.title || item.filename || `图片 ${item.id}`}</a>
                <button
                  className="material-delete"
                  type="button"
                  aria-label={`删除 ${item.title || item.filename || `图片 ${item.id}`}`}
                  onClick={() => void handleDeleteMaterial(item)}
                  disabled={readonly || deletingMaterialId === item.id}
                >
                  {deletingMaterialId === item.id ? <span className="spinner tiny" aria-hidden="true" /> : '×'}
                </button>
              </div>
            ))}
            {uploadingMaterials.map((item) => (
              <div className={`material-chip ${item.status}`} key={item.id}>
                <span className="spinner tiny" aria-hidden="true" />
                <span>{item.status === 'failed' ? `${item.filename} 上传失败` : `${item.filename} 上传中`}</span>
              </div>
            ))}
          </div>
          <div className="bot-picker">
            <div className="bot-picker-top">
              <strong>销售微信/IP</strong>
              <label className="checkbox-row">
                <input type="checkbox" checked={allSelected} onChange={(event) => toggleAllBots(event.target.checked)} disabled={readonly || busy || selectableBots.length === 0} />
                <span>全选</span>
              </label>
            </div>
            <div className="bot-list">
              {selectableBots.map((bot) => (
                <label className="checkbox-row bot-option" key={bot.wechatRecordId}>
                  <input
                    type="checkbox"
                    checked={selectedBotIds.includes(bot.wechatRecordId)}
                    onChange={(event) => toggleBot(bot.wechatRecordId, event.target.checked)}
                    disabled={readonly || busy}
                  />
                  <span>{formatBot(bot)}</span>
                </label>
              ))}
              {selectableBots.length === 0 ? <div className="campaign-empty compact">没有可用的销售微信/IP</div> : null}
            </div>
          </div>
          <button className="button primary large" type="button" onClick={handleOrganize} disabled={readonly || busy || selectedBotIds.length === 0}>
            {organizeStep ? '整理中...' : '帮我整理活动'}
          </button>
          {organizeStep ? <ProgressNotice label={organizeStep} /> : null}
        </section>

        <section className="campaign-step compact ai-step">
          <div className="campaign-step-title">
            <span>2</span>
            <h2>AI 整理</h2>
          </div>
          <div className="campaign-chat">
            <div className="campaign-chat-bubble assistant">
              {campaign
                ? '我已经把活动整理成右侧字段了，你可以直接改。'
                : '先填活动文案和销售微信/IP，然后点“帮我整理活动”。'}
            </div>
            {campaign?.extractionWarnings?.map((warning) => (
              <div className="campaign-chat-bubble warning" key={warning}>{warning}</div>
            ))}
          </div>
          <label>
            <span>补充要求</span>
            <textarea
              value={tone}
              onChange={(event) => {
                setTone(event.target.value);
                patchDraft({ audience_hint: event.target.value });
              }}
              rows={4}
              disabled={readonly || busy}
            />
          </label>
        </section>

        <section className="campaign-step editor compact">
          <div className="campaign-step-title">
            <span>3</span>
            <h2>人工编辑</h2>
          </div>
          <div className="campaign-editor-grid">
          <label>
            <span>活动标题</span>
            <input value={inputValue(draft.title || campaign?.title)} onChange={(event) => patchDraft({ title: event.target.value })} disabled={readonly || busy} />
          </label>
          <label>
            <span>主推品类</span>
            <input value={inputValue(draft.main_category)} onChange={(event) => patchDraft({ main_category: event.target.value })} disabled={readonly || busy} />
          </label>
          <label>
            <span>价格机制</span>
            <textarea value={inputValue(draft.price_mechanism)} onChange={(event) => patchDraft({ price_mechanism: event.target.value })} rows={2} disabled={readonly || busy} />
          </label>
          <label>
            <span>核心卖点</span>
            <textarea value={inputValue(draft.selling_point)} onChange={(event) => patchDraft({ selling_point: event.target.value })} rows={2} disabled={readonly || busy} />
          </label>
          <label>
            <span>赠品/权益</span>
            <textarea value={inputValue(draft.bonus)} onChange={(event) => patchDraft({ bonus: event.target.value })} rows={1} disabled={readonly || busy} />
          </label>
          <label>
            <span>有效期</span>
            <input
              value={inputValue(draft.valid_until || validUntil)}
              onChange={(event) => {
                setValidUntil(event.target.value);
                patchDraft({ valid_until: event.target.value });
              }}
              placeholder="例如 2026-07-31"
              disabled={readonly || busy}
            />
          </label>
          </div>
          <button className="button" type="button" onClick={() => void handleSaveEdits()} disabled={readonly || busy || selectedBotIds.length === 0}>
            保存编辑
          </button>
        </section>
        </div>

        <section className="campaign-step preview">
          <div className="campaign-step-title">
            <span>4</span>
            <h2>预览文案到发送</h2>
          </div>
          <div className="preview-actions">
            <label>
              <span>dry run 数量</span>
              <input value={previewLimit} onChange={(event) => setPreviewLimit(event.target.value)} disabled={readonly || busy || previewAll} />
            </label>
            <label className="campaign-compact-number">
              <span>并发</span>
              <input
                type="number"
                min={1}
                max={PREVIEW_CONCURRENCY_MAX}
                step={1}
                value={previewConcurrency}
                onChange={(event) => setPreviewConcurrency(event.target.value)}
                onBlur={() => setPreviewConcurrency(String(parsePreviewConcurrency(previewConcurrency)))}
                disabled={readonly || busy}
              />
            </label>
            <label className="campaign-inline-check">
              <input
                type="checkbox"
                checked={previewAll}
                onChange={(event) => setPreviewAll(event.target.checked)}
                disabled={readonly || busy}
              />
              <span>全部白名单{candidateCount === null ? '' : `（共 ${candidateCount} 人）`}</span>
            </label>
            <button className="button primary" type="button" onClick={handlePreview} disabled={!canPreview || busy} title={!canPreview ? previewBlockedReason ?? undefined : undefined}>
              {previewStep ? '生成中...' : '生成文案 dry run'}
            </button>
            <button className="button" type="button" onClick={handleTextRepairPreview} disabled={!canPreview || busy} title={!canPreview ? previewBlockedReason ?? undefined : undefined}>
              补发缺失文案预览
            </button>
            <button className="button danger" type="button" onClick={() => setDirectSendConfirmOpen(true)} disabled={!canPreview || busy} title={!canPreview ? previewBlockedReason ?? undefined : undefined}>
              生成并发送
            </button>
            {preview?.status === 'running' ? (
              <>
                <button className="button" type="button" onClick={() => void handlePausePreview()} disabled={readonly || nonPreviewBusy}>暂停</button>
                <button className="button danger secondary-danger" type="button" onClick={() => void handleCancelPreview()} disabled={readonly || nonPreviewBusy}>停止</button>
              </>
            ) : null}
            {preview?.status === 'paused' ? (
              <>
                <button className="button primary" type="button" onClick={() => void handleResumePreview()} disabled={readonly || busy}>继续 dry run</button>
                <button className="button danger secondary-danger" type="button" onClick={() => void handleCancelPreview()} disabled={readonly || busy}>停止</button>
              </>
            ) : null}
            <button className="button danger" type="button" onClick={() => setSendConfirmOpen(true)} disabled={!canSend || busy} title={!canSend ? sendBlockedReason ?? undefined : undefined}>
              发送
            </button>
          </div>
          {previewBlockedReason ? <div className="campaign-hint">{previewBlockedReason}</div> : null}
          {!previewBlockedReason && sendBlockedReason ? <div className="campaign-hint">{sendBlockedReason}</div> : null}
          {previewStep ? <ProgressNotice label={previewStep} /> : null}
          <PreviewSummary preview={preview} />
          <PreviewTable items={previewItems} runItems={runItems} />
          <div className="preview-pagination">
            <button className="button" type="button" disabled={!preview || previewPage === 0 || nonPreviewBusy} onClick={() => void loadPreviewPage(previewPage - 1)}>上一页</button>
            <span>第 {previewPage + 1} 页 · 每页 {PREVIEW_PAGE_SIZE} 条</span>
            <button
              className="button"
              type="button"
              disabled={!preview || nonPreviewBusy || previewItems.length < PREVIEW_PAGE_SIZE}
              onClick={() => void loadPreviewPage(previewPage + 1)}
            >
              下一页
            </button>
          </div>
        </section>
      </section>

      {sendConfirmOpen ? (
        <div className="campaign-modal-backdrop" role="presentation">
          <div className="campaign-modal" role="dialog" aria-modal="true" aria-labelledby="send-confirm-title">
            <h2 id="send-confirm-title">确认发送</h2>
            <p>{preview?.summary?.mode === 'text_repair'
              ? `本次将只补发文字给 ${preview?.eligibleCount ?? 0} 个可发送客户，不会重复发送活动图片。发送后系统会按节奏排队执行。`
              : `本次将按 dry run 结果发送给 ${preview?.eligibleCount ?? 0} 个可发送客户。发送后系统会按节奏排队执行。`}</p>
            <div className="campaign-modal-actions">
              <button className="button" type="button" onClick={() => setSendConfirmOpen(false)} disabled={busy}>取消</button>
              <button className="button danger" type="button" onClick={handleSend} disabled={busy}>确认发送</button>
            </div>
          </div>
        </div>
      ) : null}
      {directSendConfirmOpen ? (
        <div className="campaign-modal-backdrop" role="presentation">
          <div className="campaign-modal" role="dialog" aria-modal="true" aria-labelledby="direct-send-confirm-title">
            <h2 id="direct-send-confirm-title">确认生成并发送</h2>
            <p>系统会逐个客户真实跑判断官和销售 AI，通过一个就立刻发送文字和活动图，不会等待整批 dry run 完成。</p>
            <div className="campaign-modal-actions">
              <button className="button" type="button" onClick={() => setDirectSendConfirmOpen(false)} disabled={busy}>取消</button>
              <button className="button danger" type="button" onClick={handleDirectRunAndSend} disabled={busy}>确认生成并发送</button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function ProgressNotice({ label }: { label: string }) {
  return (
    <div className="progress-notice" role="status" aria-live="polite">
      <div className="progress-row">
        <span className="spinner" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="progress-track" aria-hidden="true">
        <span />
      </div>
    </div>
  );
}

function PreviewSummary({ preview }: { preview: CampaignPreview | null }) {
  return (
    <div className="campaign-stat-grid">
      <Stat label="候选客户" value={preview?.candidateCount ?? 0} />
      <Stat label="可发送" value={preview?.eligibleCount ?? 0} />
      <Stat label="不可发送" value={preview?.excludedCount ?? 0} />
      <Stat label="需查看" value={0} />
      <Stat label="状态" value={preview ? statusLabel(preview.status) : '-'} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="campaign-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function previewError(preview: CampaignPreview): string {
  return typeof preview.summary?.error === 'string' ? preview.summary.error : 'dry run 后台任务失败';
}

function PreviewTable({ items, runItems }: { items: CampaignPreviewItem[]; runItems: CampaignRunItem[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const runItemByFriendId = useMemo(() => {
    const map = new Map<number, CampaignRunItem>();
    for (const item of runItems) map.set(item.friendId, item);
    return map;
  }, [runItems]);
  if (items.length === 0) {
    return <div className="campaign-empty compact">还没有 dry run 结果</div>;
  }
  return (
    <div className="preview-table simple">
      <div className="preview-row header">
        <span>客户</span>
        <span>销售微信</span>
        <span>状态</span>
        <span>将发送的文案</span>
      </div>
      {items.map((item) => {
        const expanded = expandedId === item.id;
        const runItem = runItemByFriendId.get(item.friendId);
        const displayStatus = runItem?.status ?? (item.eligible ? 'eligible' : 'blocked');
        const statusClass = runItem
          ? (runItem.status === 'sent' || runItem.status === 'replied' ? 'sent' : runItem.status)
          : (item.eligible ? 'ready' : 'blocked');
        const previewDetail = {
          gateReason: item.excludeReason,
          eligible: item.eligible,
          runStatus: runItem?.status,
          sentAt: runItem?.sentAt,
          runSkipReason: runItem?.skipReason,
          activationAngle: item.activationAngle,
          judgeInput: item.judgeInput,
          judgeResult: item.judgeResult,
        };
        return (
          <div className="preview-row" key={item.id}>
            <span className="preview-customer" title={String(item.customerName || item.friendId)}>{item.customerName || item.friendId}</span>
            <span className="preview-bot">{item.botWechatRecordId}</span>
            <span className={`campaign-status ${statusClass}`}>{statusLabel(displayStatus)}</span>
            <div className="preview-copy">
              <span className="preview-copy-main">{item.sampleMessages?.join('\n\n') || runItem?.skipReason || item.excludeReason || '-'}</span>
              <button
                className="preview-detail-toggle"
                type="button"
                onClick={() => setExpandedId(expanded ? null : item.id)}
              >
                {expanded ? '收起详情' : '查看详情'}
              </button>
              {expanded ? (
                <pre className="preview-detail-json">{JSON.stringify(previewDetail, null, 2)}</pre>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

async function pollPreviewUntilDone(
  campaignId: number,
  previewId: number,
  previewPageRef: { current: number },
  onAttempt: (attempt: number) => void,
  onUpdate: (response: Awaited<ReturnType<typeof getCampaignPreview>>) => void,
): Promise<Awaited<ReturnType<typeof getCampaignPreview>>> {
  for (let attempt = 0; attempt < 450; attempt += 1) {
    if (attempt > 0) {
      onAttempt(attempt);
    }
    const response = await getCampaignPreview(campaignId, previewId, {
      limit: PREVIEW_PAGE_SIZE,
      offset: previewPageRef.current * PREVIEW_PAGE_SIZE,
    });
    onUpdate(response);
    if (response.preview.status !== 'running') {
      return response;
    }
    await new Promise((resolve) => setTimeout(resolve, 2_000));
  }
  throw new Error('dry run 仍在运行，请稍后刷新预览结果');
}

async function pollExtractionUntilDone(
  campaignId: number,
  onAttempt: (attempt: number) => void,
): Promise<CampaignDetailResponse> {
  for (let attempt = 0; attempt < 450; attempt += 1) {
    if (attempt > 0) {
      onAttempt(attempt);
    }
    const response = await getCampaign(campaignId);
    const status = extractStatus(response.campaign);
    if (status !== 'running') {
      return response;
    }
    await new Promise((resolve) => setTimeout(resolve, 2_000));
  }
  throw new Error('活动整理仍在运行，请稍后刷新活动字段');
}

function extractStatus(campaign: Campaign): string {
  return campaign.fieldConfidence?.__extract_status ?? '';
}

function extractError(campaign: Campaign): string {
  return campaign.fieldConfidence?.__extract_error ?? '';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = typeof reader.result === 'string' ? reader.result : '';
      resolve(value.includes(',') ? value.slice(value.indexOf(',') + 1) : value);
    };
    reader.onerror = () => reject(reader.error ?? new Error('图片读取失败'));
    reader.readAsDataURL(file);
  });
}
